import { useCallback, useEffect } from "react";
import { Link } from "react-router";
import { styled } from "styled-components";

import { SectionTitleUnderlined } from "common/components";
import { breakpoints, mediaStyles } from "common/styles";
import { useDispatch, useMemoisedSelector, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { getUserId } from "domains/authentication/selectors";
import { getSiteLanguage } from "domains/language/selectors";
import { getEmergingThemesHasNextPage } from "domains/monitor/emergingThemes/selectors";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { fetchLatestKnowledge } from "domains/monitor/latestKnowledge/actions";
import {
  getLatestKnowledgeHasNextPage,
  getLatestKnowledgeIsPending,
  getLatestKnowledgeItems,
  getLatestKnowledgeOffset,
} from "domains/monitor/latestKnowledge/selectors";
import { KnowledgeList } from "domains/publication/components/KnowledgeList";
import { useIsPhone } from "domains/viewport/useMedia";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { getCanAccessMonitor } from "entities/permissions/selectors";
import { getFollowedTopicsIds } from "entities/topics/selectors";

const StyledContainer = styled.div`
  display: flex;
  flex: 5;
  flex-direction: column;
  box-sizing: border-box;
  ${mediaStyles(({ spacing }) => `margin: 0 ${spacing}rem;`)};

  @media ${breakpoints.extraLarge} {
    flex: 4;
  }
`;

const StyledSeeAllButton = styled(Link)`
  margin: 0 0 2rem 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.blue};
  font-weight: bold;
  text-decoration: none;
`;

export const LatestKnowledge = () => {
  const dispatch = useDispatch();
  const isMobile = useIsPhone();
  const items = useMemoisedSelector(getLatestKnowledgeItems, createKnowledgesSelector);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const isPending = useSelector(getLatestKnowledgeIsPending);
  const offset = useSelector(getLatestKnowledgeOffset);
  const hasNextPage = useSelector(getLatestKnowledgeHasNextPage);
  const emergingThemesHasNextPage = useSelector(getEmergingThemesHasNextPage);
  const language = useSelector(getSiteLanguage);
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);
  const followingTopicsIds = useSelector(getFollowedTopicsIds);
  const userId = useSelector(getUserId);
  const translate = useTranslate();

  const displaySeeAllButton = isMobile || !emergingThemesHasNextPage;

  const followingTopicsDependency = followingTopicsIds.join();
  const selectedTopicsDependency = selectedTopics.join();

  useEffect(() => {
    dispatch(fetchLatestKnowledge({ offset: 0, filters: { topic: selectedTopics } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicsDependency, language, userId, followingTopicsDependency]);

  const loadNextPage = useCallback(() => {
    if (!displaySeeAllButton) {
      dispatch(
        fetchLatestKnowledge({
          offset: offset + 1,
          filters: { topic: selectedTopics },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, selectedTopicsDependency, displaySeeAllButton]);

  const getToLink = (knowledgeId: string) => `/monitor/latest-knowledge/${knowledgeId}`;

  return (
    <StyledContainer id="latest-knowledge">
      <SectionTitleUnderlined isLoading={isPending}>{translate("app.monitor.latest_knowledge")}</SectionTitleUnderlined>
      {items.length === 0 && !isPending && hasAccessToMonitor ? (
        <span>
          {translate(selectedTopics.length === 0 ? "app.monitor.empty_message" : "app.monitor.empty_message_filter")}
        </span>
      ) : (
        <KnowledgeList
          items={items}
          isPending={isPending}
          hasNextPage={hasNextPage && !displaySeeAllButton}
          loadNextPage={loadNextPage}
          getToLink={getToLink}
          trackPage="Monitor"
          lockTiles={!hasAccessToMonitor}
        />
      )}
      {/* To prevent one infinite list while the other is loaded */}
      {displaySeeAllButton && items.length > 0 && hasNextPage && (
        <StyledSeeAllButton to="/monitor/latest-knowledge">{translate("app.topic.see_all")}</StyledSeeAllButton>
      )}
    </StyledContainer>
  );
};
